/**
 * 表示項目キーの正本。
 *
 * 型定義をここに置く理由: 中立モジュール（LaneCard / metricHelpers）がこれらの型と
 * カテゴリ定義を参照する一方、DisplayMetricToggle もカテゴリ定義を参照する。
 * 型が page 側にあると相互参照になるため、正本を中立側に置き page から re-export する。
 */

export type ValueMetric = 'quantity' | 'weight' | 'volume';

/**
 * 車両リスト専用の表示タグ。容量 3 指標(ValueMetric)とは別扱いにする。
 * ValueMetric に混ぜると TriMetricValue と容量サマリの「3 指標」前提が崩れる。
 * courseNumber はカード配車の行の先頭に出すコース番号（サンプル MatchingPresenter.tsx:1508。log_web_issues#352）
 */
export type VehicleMetric = 'courseNumber' | 'pickupTag' | 'deliveryTag' | 'vehicleTypeTag' | 'grossProfit';

export type FixedInfoMetric =
  | 'origin'
  | 'destination'
  | 'section_pref'
  | 'section_city'
  | 'origin_address'
  | 'dest_address'
  | 'shipper'
  | 'shipper_contact'
  | 'shipper_phone'
  | 'shipper_email'
  | 'time_window'
  | 'invoice'
  | 'remarks'
  | 'product'
  | 'product_code'
  | 'req_vehicle_type'
  | 'req_truck_type'
  | 'bed_height'
  | 'bed_width'
  | 'bed_length'
  | 'floor_spec'
  | 'equipment'
  | 'designated_truck'
  | 'ng_driver';

/** 会社設定の任意項目。キーに項目名を含めるので、項目の入れ替えで別項目が誤表示されない。 */
export type CustomInfoMetric = `custom:${string}`;

export type InfoMetric = FixedInfoMetric | CustomInfoMetric;

/** 配車カードのセルに出せる項目。車両リスト表示タグ(VehicleMetric)は対象外。 */
export type BaseMetric = ValueMetric | InfoMetric;

/**
 * カード表示（配車カードのセル）に指定した項目。基準キーに `card:` を付けて保持する。
 * 別の localStorage キーに分けず同じ保存値へ入れるのは、保存先を 1 本に保つため
 * （分けると「基準キーを外したのにカード表示だけ残る」不整合を面倒見る箇所が増える）。
 */
export type CardMetricKey = `card:${BaseMetric}`;

export type DisplayMetric = ValueMetric | VehicleMetric | InfoMetric | CardMetricKey;

/**
 * 旧「条件」キー。#316 で車両情報 7 項目に分解されて選択肢から消えたが、
 * localStorage には残っている。DisplayMetric に混ぜないのは、UI が選べる集合と
 * 保存値に現れ得る集合を型で区別するため。
 */
export type LegacyDisplayMetric = 'conditions';

/** localStorage から読み出した直後の値。旧キーを含み得る */
export type PersistedDisplayMetric = DisplayMetric | LegacyDisplayMetric;

export const CUSTOM_METRIC_PREFIX = 'custom:';
export const CARD_METRIC_PREFIX = 'card:';

/** セルに同時表示できる項目の上限（issue #316「配車カードのセルに表示（最大3件）」） */
export const CARD_METRIC_LIMIT = 3;

export const VALUE_METRICS: { key: ValueMetric; label: string }[] = [
  { key: 'quantity', label: '数量' },
  { key: 'weight', label: '重量' },
  { key: 'volume', label: '体積' },
];

export const VEHICLE_METRICS: { key: VehicleMetric; label: string }[] = [
  { key: 'courseNumber', label: 'コース番号' },
  { key: 'pickupTag', label: '積込タグ' },
  { key: 'deliveryTag', label: '納品タグ' },
  { key: 'vehicleTypeTag', label: '車両タイプ' },
  { key: 'grossProfit', label: '粗利' },
];

const FIXED_INFO_LABELS: Record<FixedInfoMetric, string> = {
  origin: '積地',
  destination: '降地',
  section_pref: '区間(都道府県)',
  section_city: '区間(市区町村)',
  origin_address: '積地住所',
  dest_address: '降地住所',
  shipper: '荷主名',
  shipper_contact: '荷主担当者',
  shipper_phone: '荷主電話番号',
  shipper_email: '荷主メールアドレス',
  time_window: '指定時間',
  invoice: '伝票No',
  remarks: '備考',
  product: '品名',
  product_code: '商品コード',
  req_vehicle_type: '車両タイプ',
  req_truck_type: '車種',
  bed_height: '荷台高',
  bed_width: '荷台幅',
  bed_length: '荷台長',
  floor_spec: '床仕様',
  equipment: '装置',
  designated_truck: '指定トラック',
  ng_driver: 'NGドライバー',
};

const LOCATION_KEYS: FixedInfoMetric[] = [
  'origin', 'destination', 'section_pref', 'section_city', 'origin_address', 'dest_address',
];
const ORDER_KEYS: FixedInfoMetric[] = [
  'shipper', 'shipper_contact', 'shipper_phone', 'shipper_email', 'time_window', 'invoice', 'remarks',
];
const CARGO_KEYS: FixedInfoMetric[] = ['product', 'product_code'];

/**
 * 車両情報カテゴリ。旧「条件」(designated_truck_* を 1 つのタグ列にまとめたもの) を
 * 分解した 7 項目で、migrateLegacyDisplayMetrics の展開先でもある。
 */
export const VEHICLE_INFO_METRICS: FixedInfoMetric[] = [
  'req_vehicle_type', 'req_truck_type', 'bed_height', 'bed_width', 'bed_length', 'floor_spec', 'equipment',
];
const DESIGNATION_KEYS: FixedInfoMetric[] = ['designated_truck', 'ng_driver'];

const LEGACY_CONDITIONS_METRIC: LegacyDisplayMetric = 'conditions';

const VALUE_METRIC_KEYS: ReadonlySet<string> = new Set(VALUE_METRICS.map((m) => m.key));
const FIXED_INFO_KEYS: ReadonlySet<string> = new Set(Object.keys(FIXED_INFO_LABELS));
const VEHICLE_METRIC_KEYS: ReadonlySet<string> = new Set(VEHICLE_METRICS.map((m) => m.key));

/**
 * 数値指標 (quantity/weight/volume) かどうかの判定。DisplayMetricToggle の全解除防止
 * ガードと VehicleInfoView の容量表示切替が同じ判定基準を見るための正本。
 * 各画面側にハードコードすると、指標の追加・変更時に片方だけ直し忘れて食い違う。
 */
export function isValueMetric(key: DisplayMetric): key is ValueMetric {
  return VALUE_METRIC_KEYS.has(key);
}

/**
 * 表示項目の既定値。両画面が同じキーで永続化するため定数を共有する。
 * 粗利は初期 ON (サンプル準拠)。保存値があるユーザーには効かない
 * (usePersistedJSON は localStorage が null のときだけ既定値を使う)。
 */
export const DEFAULT_DISPLAY_METRICS: DisplayMetric[] = ['quantity', 'grossProfit'];

export type DisplayMetricGroup = {
  id: 'location' | 'order' | 'cargo' | 'vehicle' | 'designation' | 'custom';
  label: string;
  items: { key: InfoMetric; label: string }[];
};

function customMetricName(key: string): string {
  return key.slice(CUSTOM_METRIC_PREFIX.length);
}

function isCustomMetric(key: string): boolean {
  return key.startsWith(CUSTOM_METRIC_PREFIX) && customMetricName(key).length > 0;
}

/** 配車カードのセルに出せる基準キーか。card: の中身と保存値の検証で使う */
function isBaseMetric(key: string): key is BaseMetric {
  return VALUE_METRIC_KEYS.has(key) || FIXED_INFO_KEYS.has(key) || isCustomMetric(key);
}

export const toCardMetricKey = (metric: BaseMetric): CardMetricKey => `${CARD_METRIC_PREFIX}${metric}`;

export const isCardMetricKey = (key: string): key is CardMetricKey => key.startsWith(CARD_METRIC_PREFIX);

const cardMetricBase = (key: CardMetricKey): BaseMetric => key.slice(CARD_METRIC_PREFIX.length) as BaseMetric;

/**
 * 保存値からカード表示対象を選択順で返す。順序を保つのは、セルの並びとチップの連番
 * (1,2,3) が「選んだ順」であることを利用者に見せるため。
 */
export function cardMetrics(saved: readonly DisplayMetric[]): BaseMetric[] {
  return saved.filter(isCardMetricKey).map(cardMetricBase);
}

/**
 * 任意項目カテゴリは会社設定が空なら生成しない（空カテゴリを描かないため）。
 * 呼び出し側は license 未取得のあいだ空配列を渡すので、その間も任意項目は出ない。
 */
export function buildDisplayMetricGroups(customInputFieldNames: string[]): DisplayMetricGroup[] {
  const fixed = (keys: FixedInfoMetric[]) => keys.map((key) => ({ key, label: FIXED_INFO_LABELS[key] }));
  const groups: DisplayMetricGroup[] = [
    { id: 'location', label: '地点・区間', items: fixed(LOCATION_KEYS) },
    { id: 'order', label: '案件情報', items: fixed(ORDER_KEYS) },
    { id: 'cargo', label: '荷物情報', items: fixed(CARGO_KEYS) },
    { id: 'vehicle', label: '車両情報', items: fixed(VEHICLE_INFO_METRICS) },
    { id: 'designation', label: '指定トラック・NGドライバー', items: fixed(DESIGNATION_KEYS) },
  ];
  if (customInputFieldNames.length > 0) {
    groups.push({
      id: 'custom',
      label: '任意項目',
      items: customInputFieldNames.map((name) => ({ key: `${CUSTOM_METRIC_PREFIX}${name}`, label: name })),
    });
  }
  return groups;
}

/**
 * 旧「条件」を車両情報 7 項目へ読み替える。conditions の位置に展開し、既に選ばれている
 * キーは足さない（重複させると activeInfoMetrics の並びが崩れる）。
 *
 * Why not localStorage を書き換える一度きりの移行にする: 保存値の書き換えは
 * effectiveDisplayMetrics 以来の「保存値は書き換えず読むたびに導出する」方針と食い違う。
 * 読み替えだけにしておけば、利用者が次にトグルを触った時点で新しい形が保存される。
 */
export function migrateLegacyDisplayMetrics(saved: readonly PersistedDisplayMetric[]): DisplayMetric[] {
  // 旧キーが無いと確かめた後なので、PersistedDisplayMetric[] は DisplayMetric[] と同じ中身
  if (!saved.includes(LEGACY_CONDITIONS_METRIC)) return [...saved] as DisplayMetric[];
  const seen = new Set<string>();
  const migrated: DisplayMetric[] = [];
  const push = (metric: DisplayMetric) => {
    if (seen.has(metric)) return;
    seen.add(metric);
    migrated.push(metric);
  };
  saved.forEach((metric) => {
    if (metric === LEGACY_CONDITIONS_METRIC) {
      VEHICLE_INFO_METRICS.forEach(push);
      return;
    }
    push(metric);
  });
  return migrated;
}

/**
 * 保存値のうち「現在の会社で意味を持つキー」だけを返す。保存値は書き換えない。
 * 他社の custom: キーを保存値に残したまま UI から隠すための導出。
 * 車両リスト指標(grossProfit)も可視項目として数える。DisplayMetricToggle が
 * バッジ件数と全解除防止をこの結果で判定するため、落とすと粗利だけ ON の状態で
 * 「可視 0 件」と誤判定される。
 * カード表示キー(card:)は数えない。基準キーと二重に数えるとバッジ件数が実際の
 * チェック数と合わなくなる。
 *
 * `includeVehicleMetrics: false` は、車両リスト表示タグのセクションごと出さない画面
 * （長距離配車・#338 C2）のため。隠した項目を数えると、バッジが画面に出ているチェックの
 * 数より多くなる。既定は true なので、渡さない `/map`・カード配車は今までどおり。
 */
export function visibleDisplayMetrics(
  saved: readonly DisplayMetric[],
  customInputFieldNames: string[],
  options: { includeVehicleMetrics?: boolean } = {},
): DisplayMetric[] {
  const { includeVehicleMetrics = true } = options;
  const available = new Set(customInputFieldNames);
  return saved.filter((key) => {
    if (isCardMetricKey(key)) return false;
    if (VEHICLE_METRIC_KEYS.has(key)) return includeVehicleMetrics;
    if (VALUE_METRIC_KEYS.has(key) || FIXED_INFO_KEYS.has(key)) return true;
    return isCustomMetric(key) && available.has(customMetricName(key));
  });
}

/**
 * 車両リスト表示タグの既定（サンプル route-planner: MapPage.tsx:555-558）。
 * 積込タグだけ OFF。保存値に車両タグが 1 つも無いときだけ補う。
 * コース番号はサンプルでは OFF 既定だが、表示を求められている機能なので ON にする（log_web_issues#352）
 */
export const DEFAULT_VEHICLE_METRICS: VehicleMetric[] = ['courseNumber', 'deliveryTag', 'vehicleTypeTag', 'grossProfit'];

/** 車両リスト表示タグかどうか。既定を補うかの判定に使う */
export const isVehicleMetric = (metric: DisplayMetric): metric is VehicleMetric => VEHICLE_METRIC_KEYS.has(metric);

/**
 * 左のトラックリストが実際に読む指標か。数値指標（容量とゲージ）と車両リスト表示タグだけで、
 * 表示情報とカード表示は読まない（VehicleInfoView / buildExpandedRowVM の参照範囲）。
 *
 * これを分けているのは表示のためではなく再描画のため。表示項目の実効集合をそのまま
 * トラックリストへ渡すと、未割当リストにしか効かない項目を切り替えただけで Set の参照が
 * 変わり、全トラック行の memo が破れる（ユーザー指摘 2026-09-01）。
 */
export const isVehicleListMetric = (metric: DisplayMetric): boolean => (
  isValueMetric(metric) || isVehicleMetric(metric)
);

/**
 * 契約: 数値指標 (quantity/weight/volume) は常に最低 1 つ有効（車両カードは常に容量と
 * ゲージを描画するため）。粗利や表示情報キーだけを残して数値指標を全解除した既存の
 * 保存値（旧バージョンで到達可能だった）に対しては quantity を有効として補って返す。
 * 車両リスト表示タグも同様に、1 つも保存されていなければ既定を補う。
 * 保存値そのものは書き換えない — 呼び出し側が読むたびにこの関数を通す非破壊な導出。
 * DisplayMetricToggle のチェック状態と VehicleInfoView の表示は、この関数を
 * 通した結果を見ることで一致させる（片方だけ導出すると表示とチェックが食い違う）。
 */
export function effectiveDisplayMetrics(metrics: readonly DisplayMetric[]): DisplayMetric[] {
  const withValue = metrics.some(isValueMetric) ? [...metrics] : [...metrics, 'quantity' as DisplayMetric];
  // 1 つでも保存されていればその集合が利用者の意思。既定で埋め戻すと OFF にできない
  return metrics.some(isVehicleMetric) ? withValue : [...withValue, ...DEFAULT_VEHICLE_METRICS];
}

/**
 * 未割当カードの積地・降地。lovable のカードと同じく、この 2 つはカード本体の
 * 既定ビューに 積/降 のラベル付きで出す（9px の補足行に混ぜると、見出しと同じ文字が
 * 並ぶだけでチェックの効果が読み取れなかった。指摘 2026/08/18）。
 *
 * 両方外したときは降地を出す。カードの見出しに当たる行が消えると、どの案件か
 * 分からなくなるため（ユーザー判断）。
 */
export function resolvePlaceMetrics(
  saved: readonly DisplayMetric[],
): { origin: boolean; destination: boolean } {
  const origin = saved.includes('origin');
  const destination = saved.includes('destination');
  if (!origin && !destination) return { origin: false, destination: true };
  return { origin, destination };
}

/** 積地・降地か。既定表示（降地）の確定が要るキーの判定に使う */
export function isPlaceMetric(metric: DisplayMetric): boolean {
  return metric === 'origin' || metric === 'destination';
}

/**
 * チェックが付いて見える集合。effectiveDisplayMetrics の補完（数値指標・車両タグ）に
 * 積地・降地の既定を重ねる。表示とチェックが食い違うと、外したはずの項目が出ている
 * 理由が読み取れない。
 */
export function checkedDisplayMetrics(saved: readonly DisplayMetric[]): DisplayMetric[] {
  const effective = effectiveDisplayMetrics(saved);
  const { destination } = resolvePlaceMetrics(saved);
  return destination && !effective.includes('destination')
    ? [...effective, 'destination']
    : effective;
}

/**
 * カードに描く表示情報キーを、カテゴリ定義順（地点・区間 → 案件情報 → … → 任意項目）で返す。
 * 静的配列を各カードに置くと custom: キーが列挙されないため、導出はここに一本化する。
 */
export function activeInfoMetrics(
  saved: readonly DisplayMetric[],
  customInputFieldNames: string[],
): InfoMetric[] {
  const savedSet = new Set<string>(saved);
  return buildDisplayMetricGroups(customInputFieldNames)
    .flatMap((group) => group.items)
    .filter((item) => savedSet.has(item.key))
    .map((item) => item.key);
}

export function infoMetricLabel(key: InfoMetric): string {
  if (isCustomMetric(key)) return customMetricName(key);
  return FIXED_INFO_LABELS[key as FixedInfoMetric];
}

/** 数値指標も含めたラベル。カード表示のチップとセルの見出しに使う */
export function displayMetricLabel(key: BaseMetric): string {
  const value = VALUE_METRICS.find((m) => m.key === key);
  return value ? value.label : infoMetricLabel(key as InfoMetric);
}

/**
 * localStorage 復元時のバリデータ（usePersistedJSON 用）。
 * custom: は会社設定と照合しない（照合すると license 取得前や他社滞在中に
 * 正しい保存値を失う）。照合は visibleDisplayMetrics の導出だけで行う。
 * 旧「条件」(conditions) を受理するのは、拒否すると保存値が丸ごと既定へ戻り、
 * 条件以外の設定まで消えるため（読み替えは migrateLegacyDisplayMetrics が行う）。
 */
export function isDisplayMetricArray(parsed: unknown): parsed is PersistedDisplayMetric[] {
  const isKnownKey = (m: string): boolean => VALUE_METRIC_KEYS.has(m)
    || VEHICLE_METRIC_KEYS.has(m)
    || FIXED_INFO_KEYS.has(m)
    || isCustomMetric(m)
    || m === LEGACY_CONDITIONS_METRIC;
  return Array.isArray(parsed)
    && parsed.every((m) => typeof m === 'string'
      && (isKnownKey(m) || (isCardMetricKey(m) && isBaseMetric(cardMetricBase(m)))));
}
